import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import Draggable from 'react-draggable';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import withChartExport from '../../../components/ChartExporting';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';
import { ViewSwitch, CardList } from '../../../components/CardViews/CardViews';
import legendIcon from '../../../assets/img/legend.svg';
import chartUtils from '../../../util/chartUtils';
import { colorMap } from '../../../util/colorMap';
import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { saveConfig, updateAvgChart, updateWidgetMode } from '../../../components/Filter/FilterActions';
import dateUtils from '../../../util/dates';
import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import barIcon from '../../../assets/img/chart-columns.svg';
import lineIcon from '../../../assets/img/chart-line.svg';
import summaryIcon from '../../../assets/img/chart-summary-below.svg';
import zoomOnIcon from '../../../assets/img/magnifier-on-01.svg';

import './ArrivalDepChart.scss';

const lineConfig = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff',
    events: {},
  },
  exporting: {
    enabled: false,
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: chartUtils.CARD_LINE_COLORS,
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {
      let tooltipHtml = '',
        s = [];
      this.points.forEach(point => {
        tooltipHtml += `<div class="tooltip-label-item"><span class="label-box" style="background: ${
          point.color
        }"></span>${point.series.name} rate: ${point.point.y.toFixed(2)} item${point.point.y != 1 ? 's' : ''}/day</div>`;
      });
      s.push(tooltipHtml);
      return s;
    }
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    showFirstLabel: true,
    showLastTickLabel: true,
    title: {
      text: null
    }
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      lineWidth: 1.5,
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        lineColor: '#fff',
        radius: 3
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      data: []
    } 
  ]
};

const barConfig = {
  chart: {
    type: 'column',
    backgroundColor: '#fff',
    events: {},
  },
  exporting: {
    enabled: false,
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: chartUtils.CARD_LINE_COLORS,
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    showFirstLabel: true,
    showLastTickLabel: true,
    labels: {
      align: 'center',
      x: 0,
      rotation: 0
    },
    title: {
      text: null
    }
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: true
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  tooltip: {
    enabled: false
  },
  plotOptions: {
    series: {
      events: {
        legendItemClick: function () {}
      }
    },
    column: {
      stacking: 'normal',
    }
  }
};

const DEFAULT_POS = { x: 95, y: 85 };

function renderNetStats(chart, stats) {
  const netStats = [];

  if (chart.options.chart.spacingBottom > 10) {
    const chartHeight = chart.plotHeight + chart.plotTop;
    const totalHeight = chart.chartHeight;
    const spacingBottom = chart.options.chart.spacingBottom;
    const legendHeight = totalHeight - chartHeight - spacingBottom;

    const rectHeight = spacingBottom - 40;

    const bgRect = chart.renderer.rect(chart.plotLeft, chartHeight + legendHeight + 20, chart.plotWidth, rectHeight)
      .attr({ 'fill': '#f0f2f5','stroke-width': 0, 'r': 8 })
      .add();

    netStats.push(bgRect);

    const titleY = chartHeight + legendHeight + 40;
    const title = chart.renderer.text(stats.title, chart.plotLeft + 10, titleY)
      .css({ fontSize: '14px', fontWeight: 'bold', color: '#333' })
      .add();

    netStats.push(title);

    let currentY = titleY + 20;

    stats.listed.forEach((itemName, index) => {
      const item = _.find(stats.data, { name: itemName });
      if (index > 0) {
        const separator = chart.renderer.path([
          'M', chart.plotLeft + 15, currentY,
          'L', chart.plotLeft + chart.plotWidth - 15, currentY
        ])
          .attr({ 'stroke-width': 1, 'stroke': '#ddd' })
          .add();

          netStats.push(separator);
          currentY += 10;
      }

      const rowY = currentY + 12;
      const diff = item.totalArrival - item.totalThroughput;

      const colorSquare = chart.renderer.rect(chart.plotLeft + 15, rowY - 10, 12, 12)
        .attr({ 'fill': item.color, 'stroke-width': 0, 'r': 2 })
        .add();

      netStats.push(colorSquare);

      const nameText = chart.renderer.text(item.name, chart.plotLeft + 32, rowY)
        .css({ fontSize: '12px', color: '#333' })
        .add();

      netStats.push(nameText);

      let inOutText;
      if (!stats.noBreakdown) {
        inOutText = `${item.totalArrival} in / ${item.totalThroughput} out`;
      } else {
        inOutText = `${item.totalArrival} arrived / ${item.totalThroughput} delivered`;
      }

      const inOut = chart.renderer.text(inOutText, 0, rowY)
        .css({ fontSize: '12px', color: '#666' })
        .add();

      const labelWidth = 110;
      const inOutBBox = inOut.getBBox();
      inOut.attr({ x: chart.plotLeft + chart.plotWidth - 15 - labelWidth - 10 - inOutBBox.width });

      netStats.push(inOut);

      const labelText = diff <= 0 ? `${diff} clearing` : `${diff} accumulating`;
      const labelColor = diff <= 0 ? '#185F34' : '#B52F2F';
      const labelBgColor = diff <= 0 ? 'rgba(24, 95, 52, 0.1)' : '#FDF1F3';

      const label = chart.renderer.text(labelText, 0, rowY)
        .css({ fontSize: '11px', color: labelColor, fontWeight: '500' })
        .add();

      const labelBBox = label.getBBox();
      const labelPadding = 5;

      const labelBg = chart.renderer.rect(
        chart.plotLeft + chart.plotWidth - 15 - labelWidth,
        rowY - 10 - labelPadding,
        labelWidth,
        14 + (labelPadding * 2)
      )
      .attr({ 'fill': labelBgColor, 'stroke-width': 0, 'r': 3 })
      .add();
      netStats.push(labelBg);

      label.attr({
        x: chart.plotLeft + chart.plotWidth - 15 - labelWidth + (labelWidth - labelBBox.width) / 2
      });
      netStats.push(label);

      currentY += 25;
    });
  }
  return netStats;
}

export class ArrivalDepChart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFullScreen: false,
      isTrendChart: true,
      isLabelsVisible: !!props.wallmode,
      isStatsVisible: true,
      allLinesOff: false,
      isListView: !props.wallmode,
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS,
      netStatsConfig: { title: '', data: [], listed: [] }
    };

    this.netStatsConfig = {title: '', data: [], listed: []};
  }

  static defaultProps = {
    data: {}
  };

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    this.setState({ isTrendChart: this.props.widgetMode === 'line' }, () => this.initChart());
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.data, this.props.data)) {
      this.calculateChartData();
      this.updateFontSize();
    }
    if (
      prevProps.dateFormat != this.props.dateFormat ||
      prevProps.timeUnit != this.props.timeUnit
    ) {
      this.updateDateFormat();
    }
    if (prevProps.widgetMode != this.props.widgetMode) {
      this.updateChartType();
    }
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  initChart() {
    if (this.chart) {
      this.chart.destroy();
    }
    const config = this.props.widgetMode === 'line' ? lineConfig : barConfig;

    if (this.props.widgetMode === 'bar') {
      let that = this;
      config.tooltip.formatter = function () {
        return that.tooltipFormatter(this, that);
      };
      config.plotOptions.series.events.legendItemClick = function () {
        setTimeout(() => that.updateFontSize(), 0);
      }
    }

    const updateLinesOff = () => this.updateLinesOff();
    config.plotOptions = config.plotOptions || {};
    config.plotOptions.series = config.plotOptions.series || {};
    config.plotOptions.series.events = { ...config.plotOptions.series.events, show: updateLinesOff, hide: updateLinesOff };

    config.exporting = { ...config.exporting, ...this.props.chartExporting };
    this.chart = new Highcharts['Chart'](`arrival_dep_chart_container`, config);

    this.updateDateFormat();
    this.calculateChartData();
    this.updateFontSize();
    this.updateLinesOff();
  }

  updateLinesOff() {
    if (!this.chart) {
      return;
    }
    const named = this.chart.series.filter(s => s.options.showInLegend);
    const allLinesOff = !!named.length && named.every(s => !s.visible);
    if (allLinesOff !== this.state.allLinesOff) {
      this.setState({ allLinesOff }, () => this.updateFontSize());
    }
  }

  tooltipFormatter(chartScope, componentScope) {
    const point = chartScope;
    const userOptions = point.series.userOptions;
    const labelFormat = componentScope.getDateFormatMoment();
    const netValue = point.point.options.arrival - point.point.options.throughput;
    const noBreakdown = userOptions.name === userOptions.stack;

    const subheader = `<div class="heading"><div>${userOptions.name}</div><div>${moment(point.x).format(labelFormat)}${noBreakdown ? '' : ` · ${userOptions.stack}`}</div></div>`
    const header = `<div class="demand-vs-capability-header"><div class="circle-dot" style="background: ${point.color}"></div>${subheader}</div>`;
    const arrived = `<div class="demand-vs-capability-body"><div>Arrived</div><div>${point.point.options.arrival} items</div></div>`;
    const delivered = `<div class="demand-vs-capability-body"><div>Delivered</div><div>${point.point.options.throughput} items</div></div>`;
    const percentage = `<div class="demand-vs-capability-body"><div>Share of ${userOptions.stack.toLowerCase()}</div><div>${point.percentage.toFixed(2)} %</div></div>`;
    const total = `<div class="demand-vs-capability-body"><div>Total ${userOptions.stack.toLowerCase()}</div><div>${point.total} items</div></div>`;
    const net = `<div class="demand-vs-capability-net"><div>Net</div><div style="color: ${ netValue > 0 ? '#B52F2F' : '#185F34'};">${netValue > 0 ? `+${netValue} accumulating` : `${netValue} clearing`}</div></div>`
    const tooltip = `<div class="demand-vs-capability-tooltip" style="margin: 8px 8px 0;">${header}<hr/>${arrived}${delivered}${percentage}${total}<hr/>${net}</div>`;

    return tooltip;
  }

  updateChartType() {
    this.setState({ isTrendChart: this.props.widgetMode === 'line' }, () => this.initChart());
  }

  getDateFormatMoment() {
    const momentFormat = this.props.dateFormat || 'DD MMM YYYY';
    const timeUnit = this.props.timeUnit || 'month';
    let labelFormat;
    
    switch (timeUnit) {
      case 'day':
      case 'week':
      case '2weeks':
        labelFormat = momentFormat.replace('YYYY', '').trim();
        break;
      case 'month':
      default:
        labelFormat = momentFormat.replace('DD', '').trim();
        break;
    }

    return labelFormat;
  }

  getDateFormat() {
    if (!this.props.dateFormat) return;
    const format = dateUtils.createHighchartFormats(this.props.dateFormat);
    const timeUnit = this.props.timeUnit || 'month';

    let labelFormat;
    switch (timeUnit) {
      case 'day':
        labelFormat = format.day;
        break;
      case 'week':
      case '2weeks':
        labelFormat = format.week;
        break;
      case 'month':
      default:
        labelFormat = format.month;
        break;
    }

    return labelFormat;
  }

  updateDateFormat() {
    if (!this.chart || !this.props.dateFormat) {
      return;
    } 
    
    const labelFormat = this.getDateFormat();

    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: {
          millisecond: labelFormat,
          second: labelFormat,
          minute: labelFormat,
          hour: labelFormat,
          day: labelFormat,
          week: labelFormat,
          month: labelFormat
        }
      }
    });
  }

  buildBarSeries() {
    const showInLegend = {};
    const series = [];

    const breakdownOptions = _(this.props.data).flatMap('data').flatMap('breakdown').compact().uniqBy('x').value();

    this.props.data.forEach((group, index) => {
      group.data.forEach(d => {
        if (breakdownOptions && breakdownOptions.length) {
          _.forEach(breakdownOptions, opt => {
            const b = _.find(d.breakdown, { x: opt.x });
            const s = _.find(series, { name: opt.x, stack: group.name });
            if (s) {
              s.data.push({ x: d.x, y: b?.total || 0, arrival: b?.arrival || 0, throughput: b?.throughput || 0 });
            } else {
              series.push({
                name: opt.x,
                color: opt.color || colorMap('default'),
                stack: group.name,
                id: !showInLegend[opt.x] ? `category-${opt.x}` : undefined,
                linkedTo: !showInLegend[opt.x] ? undefined : `category-${opt.x}`,
                showInLegend: !showInLegend[opt.x],
                data: [{ x: d.x, y: b?.total || 0, arrival: b?.arrival || 0, throughput: b?.throughput || 0 }]
              });
              showInLegend[opt.x] = true;
            }
          });
        } else {
          const s = _.find(series, { name: group.name });
          if (s) {
            s.data.push({ x: d.x, y: d.total, arrival: d.totalArrival, throughput: d.totalThroughput });
          } else {
            series.push({
              ...group,
              stack: group.name,
              showInLegend: true,
              color: chartUtils.CARD_LINE_COLORS[index],
              data: [{ x: d.x, y: d.total, arrival: d.totalArrival, throughput: d.totalThroughput }]
            });
          }
        }
      });
    });
    return series;
  }

  calculateChartData() {
    if (!this.chart || !this.props.dateFormat || !this.props.data || !this.props.data.length) {
      return;
    } 
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (!this.props.data.length) {
      return false;
    }

    const series = this.state.isTrendChart
      ? this.props.data.map((group, index) => chartUtils.trendLineSeries({ ...group, showInLegend: true, color: chartUtils.CARD_TREND_COLORS[index] }, false))
      : this.buildBarSeries();

    series.forEach(s => this.chart.addSeries(s, false));

    this.setNetStatsConfig(series, this.props.data[0].periods);

    this.chart.update({
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: this.props.data[0].data[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
    this.chart.redraw();
  }

  getBreakdownByName() {
    const { breakdownBy, customFields, app } = this.props;
    if (breakdownBy === 'labels') {
      switch (app) {
        case 'azure': return 'Work item types';
        case 'asana': return 'Tags';
        case 'jira': return 'Priorities';
        case 'trello':
        default: return 'Labels';
      }
    } else if (breakdownBy && customFields) {
      const customField = _.find(customFields, { extId: breakdownBy });
      if (customField) {
        return customField.name;
      }
    } 
  }

  setNetStatsConfig(chartSeries, periods) {
    const series = this.state.isTrendChart ? this.buildBarSeries() : chartSeries;
    const { timeUnit } = this.props;
    const breakdownByName = this.getBreakdownByName();

    let rangeLabel = 'All time';
    let periodsNumber = '';
    switch (timeUnit) {
      case 'day': rangeLabel = `${periods}-days`; break;
      case 'week': rangeLabel = `${periods}-weeks`; break;
      case '2weeks': rangeLabel = `${periods * 2}-weeks`; break;
      case 'month': rangeLabel = `${periods}-month${ periods > 1 ? 's' : '' }`; break;
      default: rangeLabel = 'All time'
    }

    this.netStatsConfig.title = `${rangeLabel} net accumulation${breakdownByName ? ` by ${breakdownByName}`: ''}`;
    this.netStatsConfig.data = _.uniqBy(series, 'name').map(s => ({
      name: s.name,
      color: s.color,
      totalArrival: s.totalArrival || _.sumBy(s.data, 'arrival'),
      totalThroughput: s.totalThroughput || _.sumBy(s.data, 'throughput'),
    }));
    this.netStatsConfig.listed = _.map(this.netStatsConfig.data, 'name');
    this.netStatsConfig.noBreakdown = !breakdownByName;
    this.setState({ netStatsConfig: { ...this.netStatsConfig } });
  }

  updateNetStatsVisibility() {
    if (this.chart && this.netStatsConfig.data) {
      this.netStatsConfig.listed = this.chart.series
        .filter(s => s.visible && s.options.showInLegend)
        .map(s => s.name);
      this.setState({ netStatsConfig: { ...this.netStatsConfig } });
    }
  }

  updateFontSize = () => {
    if (!this.chart) {
      return;
    }

    const { isLabelsVisible, isFullScreen, isTrendChart } = this.state;
    const showLabels = isFullScreen || !isTrendChart;
    const showLegend = isLabelsVisible || this.state.allLinesOff;

    const that = this;
    const tooltip = isTrendChart ? lineConfig.tooltip : isFullScreen ? {
      shared: false,
      useHTML: true,
      enabled: true,
      formatter: function () { return that.tooltipFormatter(this, that); }
    } : { enabled: false };

    this.updateNetStatsVisibility();

    const netStatsConfig = this.netStatsConfig;

    this.chart.update({
      exporting: {
        enabled: isFullScreen,
        chartOptions: {
          title: {
            text: 'Arrival & throughput rates',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },
          legend: {
            enabled: true
          },
          yAxis: {
            labels: {
              enabled: true
            }
          },
          chart: {
            events: {
              redraw: function () {
                renderNetStats(this, netStatsConfig);
              },
              load: function () {
                renderNetStats(this, netStatsConfig);
              }
            }
          }
        }
      },
      legend: {
        ...chartUtils.cardLegend(isFullScreen),
        enabled: showLegend,
        useHTML: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        labelFormatter: isTrendChart ?
          function () { return `<span class="color:#333333;font-size:12px;font-weight:normal">${this.name} rate</span>` } :
          function () { return `<span class="color:#333333;font-size:12px;font-weight:normal">${this.name}</span>` }
      },
      tooltip: tooltip,
      yAxis: {
        gridLineWidth: +isFullScreen,
        labels: {
          enabled: showLabels
        },
        title: {
          text: null
        },
        showFirstLabel: false
      }
    }, true, false, false);

    requestAnimationFrame(() => {
      if (this.chart) {
        this.chart.reflow();
      }
    });
  };

  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullScreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          left: undefined,
          isFullScreen: false,
          isLabelsVisible: this.labelsBeforeFocus,
          pos: DEFAULT_POS
        },
        () => this.updateFontSize()
      );
    } else {
      this.labelsBeforeFocus = this.state.isLabelsVisible;
      this.setState(
        {
          width: '100%',
          height: '100%',
          left: -10,
          isFullScreen: true,
          isLabelsVisible: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  handleSwitch = () => {
    this.setState({ isTrendChart: !this.state.isTrendChart }, () => {
      this.props.updateWidgetMode(this.state.isTrendChart ? 'line' : 'bar');
      this.props.saveConfig();
    });
  };

  getView() {
    if (this.state.isListView) {
      return 'list';
    }
    return this.state.isTrendChart ? 'line' : 'bar';
  }

  handleView = view => {
    if (view === 'list') {
      this.setState({ isListView: true });
      return;
    }
    this.setState({ isListView: false });
    if ((view === 'line') !== this.state.isTrendChart) {
      this.handleSwitch();
    }
  };

  renderList() {
    const { netStatsConfig } = this.state;
    const names = _.map(netStatsConfig.data, 'name');
    return (
      <div className={`view-list view-list--net-stats ${netStatsConfig.noBreakdown ? 'view-list--net-total' : ''}`}>
        {this.renderNetStatsRows(netStatsConfig.noBreakdown ? names.slice(0, 1) : names)}
      </div>
    );
  }

  handleToggleStats = () => {
    this.setState({ isStatsVisible: !this.state.isStatsVisible }, () => this.updateFontSize());
  }

  renderNetStatsRows(names) {
    const { netStatsConfig } = this.state;
    return names.map((itemName) => {
      const item = _.find(netStatsConfig.data, { name: itemName });
      if (!item) return null;
      const diff = item.totalArrival - item.totalThroughput;
      const inOutText = netStatsConfig.noBreakdown
        ? `${item.totalArrival} arrived / ${item.totalThroughput} delivered`
        : `${item.totalArrival} in / ${item.totalThroughput} out`;
      const labelText = diff <= 0 ? `${diff} clearing` : `${diff} accumulating`;
      return (
        <div key={itemName} className="net-stats-row">
          {/* a span: the card's older rules stretch every div in it to its full width and height, which pushed the
              names and numbers out of sight beside a dot with no height (3190 EX 5) */}
          <span className="net-stats-color" style={{ background: item.color }} />
          <span className="net-stats-name">{item.name}</span>
          <span className="net-stats-inout">{inOutText}</span>
          <span
            className="net-stats-label"
            style={{
              color: diff <= 0 ? '#185F34' : '#B52F2F',
              background: diff <= 0 ? 'rgba(24, 95, 52, 0.06)' : 'rgba(181, 47, 47, 0.06)',
            }}
          >
            {labelText}
          </span>
        </div>
      );
    });
  }

  renderNetStatsPanel = () => {
    const {
      isTrendChart, isFullScreen, isStatsVisible, netStatsConfig,
    } = this.state;
    if (isTrendChart || !isFullScreen || !isStatsVisible || netStatsConfig.listed.length === 0 || !this.props.data.length) {
      return null;
    }
    return (
      <div className="net-stats-panel">
        <div className="net-stats-title">{netStatsConfig.title}</div>
        <div className="net-stats-panel-scroll">
          {this.renderNetStatsRows(netStatsConfig.listed)}
        </div>
      </div>
    );
  };

  render() {
    const loading = !Array.isArray(this.props.data);
    const noData = !loading && !this.props.data.length;
    const shape = this.state.isTrendChart ? 'line' : 'columns';
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box long_title rates ${this.state.isFullScreen ? 'fullscreen' : ''} ${loading || noData ? 'is-empty' : ''}`}
          style={{
            width: this.state.width,
            height: this.state.height,
            left: this.state.left,
            position: this.state.isFullScreen ? 'absolute' : ''
          }}
        >
          <div className="box_white_title">
            <h3>Arrival & throughput rates</h3>
            {/* the list's period and breakdown, under the card's name like Process Metrics' "Calculated averages" */}
            {this.state.isListView && this.state.netStatsConfig.title && (
              <p className="card-subtitle">{this.state.netStatsConfig.title}</p>
            )}
            <div className='right-actions'>
              {/* the card's three views in one control: the list of the latest rates, the trend and the bars; the Net
                  Accumulation summary under the focused bar view shows and hides with its own button */}
              <ViewSwitch
                views={[{ id: 'list', icon: legendIcon }, { id: 'line', icon: lineIcon }, { id: 'bar', icon: barIcon }]}
                active={this.getView()}
                onChange={this.handleView}
              />
              {
                !this.state.isTrendChart && this.state.isFullScreen && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="statsTooltip" className="tooltip_dark">
                        {this.state.isStatsVisible ? 'Hide net accumulation' : 'Show net accumulation'}
                      </Tooltip>
                    }>
                    <Button variant='secondary' size='sm' className={`btn-action card-toggle ${this.state.isStatsVisible ? 'active' : ''}`} onClick={this.handleToggleStats}>
                      <img src={summaryIcon} alt="type" />
                    </Button>
                  </OverlayTrigger>
                )
              }
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="focusTooltip" className="tooltip_dark">
                    {this.state.isFullScreen ? 'Restore previous layout' : 'Focus arrival & throughput rates'}
                  </Tooltip>
                }>
                <Button variant='secondary' size='sm' className={`btn-action ${this.state.isFullScreen ? 'fullscreen' : ''}`} onClick={this.handleResize}>
                  <img src={this.state.isFullScreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
                </Button>
              </OverlayTrigger>
              {/* in focus the chart can be downloaded, from the header like the card's other actions */}
              {this.state.isFullScreen && <ChartDownloadMenu getChart={() => this.chart} />}
              <Button className="btn-action" onClick={this.handleClose}>&times;</Button>
            </div>
          </div>
          <div id="arrival_dep_chart_container" />
          {/* the list, or the cards' empty list: pale pulsing rows while the rates load, "No data available" without them */}
          {this.state.isListView && (loading || noData ? <CardList columns={[]} rows={[]} loading={loading} /> : this.renderList())}
          {/* the chart's place in the line and bar views: pale pulsing lines or columns while the rates load, the same
              shape still with "No data available" when there are none (no lists selected) */}
          {!this.state.isListView && (loading || noData) && (
            <div
              className={`chart-placeholder chart-placeholder--${shape} ${loading ? 'chart-placeholder--loading' : ''}`}
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: chartUtils.placeholderMarkup(shape, loading) }}
            />
          )}
          {this.renderNetStatsPanel()}
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateAvgChart(false, 'cfd')),
    updateWidgetMode: type => dispatch(updateWidgetMode(type, 'cfd')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    wallmode: state.app.wallmode,
    app: state.app.app,
    customFields: state.app.data.customFields,
    dateFormat: state.filters.dateFormat,
    widgetMode: state.filters.cfd.widgetMode,
    breakdownBy: state.filters.cfd.enabledColors,
    timeUnit: state.filters.cfd.timeUnit
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(ArrivalDepChart));
